import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from "react-router-dom";

import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

import Navbar from '@/components/layout/Navbar';
import { api, unwrapApiResponse } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { getUserImageUrl, getUserBannerUrl, getMolduraImageUrl } from '@/utils/image-utils';

import { auth, googleProvider } from '@/utils/firebase';
import { signInWithPopup } from 'firebase/auth';

import { PerfilHeader, type PerfilRankingPosicao } from '@/components/perfil/PerfilHeader';
import { MeuPerfilQuickNav } from '@/components/perfil/MeuPerfilQuickNav';
import { MeuPerfilGeralTab } from '@/components/perfil/MeuPerfilGeralTab';
import { EditProfileModal, type ProfileSavingSection } from '@/components/perfil/EditProfileModal';
import { MoldurasModal } from '@/components/perfil/MoldurasModal';
import { PasswordModal } from '@/components/perfil/PasswordModal';
import { PaymentsHistoryModal } from '@/components/perfil/PaymentsHistoryModal';
import { PixDiscordPaymentNotice } from '@/components/payment/PixDiscordPaymentNotice';

dayjs.locale('pt-br');


export default function MeuPerfilPage() {
    const navigate = useNavigate();
    const { user: authUser, logout, isAuthenticated, refreshTokenAndUpdateUser } = useAuth();

    // States
    const [loading, setLoading] = useState(!isAuthenticated || !authUser);
    const [user, setUser] = useState<any>(authUser || null);

    const [showPaymentsModal, setShowPaymentsModal] = useState(false);
    const [myPayments, setMyPayments] = useState<any[]>([]);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [checkingPaymentId, setCheckingPaymentId] = useState<number | null>(null);
    const [myDonations, setMyDonations] = useState<any[]>([]);
    const [donationsLoading, setDonationsLoading] = useState(false);

    // Edit Profile
    const [showEditProfile, setShowEditProfile] = useState(false);
    const [savingSection, setSavingSection] = useState<ProfileSavingSection>(null);
    const [profileForm, setProfileForm] = useState({
        nome: '',
        nick: '',
        avatarPreview: '',
        avatarFile: null as File | null,
        bannerPreview: '',
        bannerFile: null as File | null,
        molduraPreview: null as string | null,
        molduraTipoPreview: null as number | null,
        selectedTags: [] as number[],
        mol_id: null as number | null,
        descricao: ''
    });
    const fileInputRef = useRef<HTMLInputElement>(null);
    const bannerInputRef = useRef<HTMLInputElement>(null);

    // Molduras (catálogo completo na memória; UI paginada no modal)
    const MOLDURAS_PAGE_SIZE = 12;
    const [showMoldurasModal, setShowMoldurasModal] = useState(false);
    const [moldurasCatalog, setMoldurasCatalog] = useState<any[]>([]);
    const [moldurasLoading, setMoldurasLoading] = useState(false);
    const [moldurasPage, setMoldurasPage] = useState(1);

    const moldurasTotalPages = Math.max(1, Math.ceil(moldurasCatalog.length / MOLDURAS_PAGE_SIZE));
    const moldurasPageItems = useMemo(() => {
        const start = (moldurasPage - 1) * MOLDURAS_PAGE_SIZE;
        return moldurasCatalog.slice(start, start + MOLDURAS_PAGE_SIZE);
    }, [moldurasCatalog, moldurasPage]);

    // Password
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordForm, setPasswordForm] = useState({
        newPassword: '',
        confirmPassword: '',
        code: ''
    });
    const [passwordStep, setPasswordStep] = useState<'request' | 'reset'>('request');

    // Messages
    const [messages, setMessages] = useState({ type: '', text: '' });

    const [rankingPosicao, setRankingPosicao] = useState<PerfilRankingPosicao | null>(null);

    const [importHistoricoStep, setImportHistoricoStep] = useState<'idle' | 'confirm' | 'running' | 'done' | 'error'>('idle');
    const [importHistoricoProgress, setImportHistoricoProgress] = useState<{ processed: number; total: number } | null>(null);

    const fetchMyPayments = async () => {
        try {
            setPaymentsLoading(true);
            const response = await api.get('meus-pagamentos');
            const res: any = (response as any)?.data ?? response;
            setMyPayments(Array.isArray(res.items) ? res.items : Array.isArray(res) ? res : []);
        } catch (error) {
            console.error('Erro ao carregar pagamentos:', error);
        } finally {
            setPaymentsLoading(false);
        }
    };

    const fetchMyDonations = async () => {
        try {
            setDonationsLoading(true);
            const response = await api.get('minhas-doacoes?limit=50&offset=0');
            const res: any = (response as any)?.data ?? response;
            setMyDonations(Array.isArray(res.items) ? res.items : Array.isArray(res) ? res : []);
        } catch (error) {
            console.error('Erro ao carregar doações:', error);
        } finally {
            setDonationsLoading(false);
        }
    };

    const handleCheckPaymentStatus = async (paymentId: number) => {
        setCheckingPaymentId(paymentId);
        try {
            const response = await api.post(`meus-pagamentos/${paymentId}/verificar`, {});
            const res: any = (response as any)?.data ?? response;
            if (res?.status === 'ativado' || res?.status === 'ja_ativado') {
                setMyPayments((prev) =>
                    prev.map((p) => (p.id === paymentId ? { ...p, status: 'completed' } : p)),
                );
                setMessage('success', 'Pagamento confirmado! VIP ativado.');
                await refreshTokenAndUpdateUser();
                await fetchUserData(true);
            } else {
                setMessage('error', 'Pagamento ainda não confirmado.');
            }
        } catch (error) {
            console.error('Erro ao verificar pagamento:', error);
            setMessage('error', 'Erro ao verificar status.');
        } finally {
            setCheckingPaymentId(null);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchUserData(!!authUser);
        }, 150);
        return () => clearTimeout(timeoutId);
    }, []);

    useEffect(() => {
        const id = user?.usr_id;
        if (!id) return;
        let cancelled = false;
        (async () => {
            try {
                const d: any = unwrapApiResponse(
                    await api.get(`xp/ranking/posicao?usr_id=${id}`, { requiresAuth: false }),
                );
                if (cancelled || typeof d?.posicao_global !== 'number') return;
                setRankingPosicao({
                    posicao_global: d.posicao_global,
                    posicao_semanal: typeof d.posicao_semanal === 'number' ? d.posicao_semanal : null,
                });
            } catch {
                if (!cancelled) setRankingPosicao(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [user?.usr_id]);

    const fetchUserData = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const res: any = await api.get('usuarios/me');
            setUser(res.data);
            return res.data as any;
        } catch (error) {
            console.error('Erro ao carregar perfil:', error);
            setMessage('error', 'Erro ao carregar dados do perfil.');
            return undefined;
        } finally {
            if (!silent) setLoading(false);
        }
    };


    useEffect(() => {
        if (showEditProfile) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showEditProfile]);

    const fetchMoldurasCatalog = async () => {
        try {
            setMoldurasLoading(true);
            const response = await api.get('usuarios/molduras');
            const res: any = (response as any)?.data ?? response;
            const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
            setMoldurasCatalog(list);
            setMoldurasPage(1);
        } catch (error) {
            console.error('Erro ao buscar molduras:', error);
            setMoldurasCatalog([]);
        } finally {
            setMoldurasLoading(false);
        }
    };

    const openMoldurasPicker = () => {
        setMoldurasPage(1);
        setShowMoldurasModal(true);
        if (moldurasCatalog.length === 0) {
            void fetchMoldurasCatalog();
        }
    };

    const handleLinkGoogle = async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const idToken = await result.user.getIdToken();

            await api.post('usuarios/me/social/google', { idToken });
            setMessage('success', 'Conta do Google vinculada com sucesso!');
            fetchUserData(true);
        } catch (err: any) {
            console.error('Erro ao vincular Google:', err);
            if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
                setMessage('error', err.message || 'Erro ao vincular conta do Google.');
            }
        }
    };

    const handleLinkDiscord = async () => {
        try {
            const redirectUri = window.location.origin + '/entrar-discord';
            const response = await api.get('auth/login/discord/url', {
                params: { redirectUri }
            });
            const res: any = (response as any)?.data ?? response;
            const url = res?.url ?? res?.data?.url;
            if (url) window.location.href = url;
            else setMessage('error', 'Não foi possível obter o link do Discord.');
        } catch (err: any) {
            console.error('Erro ao iniciar vinculo Discord:', err);
            setMessage('error', err.message || 'Erro ao conectar com Discord.');
        }
    };

    const openEditProfile = (currentUser: any) => {
        setProfileForm({
            nome: currentUser.usr_nome || '',
            nick: currentUser.usr_nick || '',
            avatarPreview: currentUser.usr_imagem ? getUserImageUrl(currentUser.usr_id, currentUser.usr_imagem) || '' : '',
            avatarFile: null,
            bannerPreview: currentUser.usr_banner ? getUserBannerUrl(currentUser.usr_id, currentUser.usr_banner) || '' : '',
            bannerFile: null,
            molduraPreview: currentUser.moldura ? getMolduraImageUrl(currentUser.moldura.mol_imagem) : null,
            molduraTipoPreview: currentUser.moldura?.mol_tipo ?? null,
            selectedTags: currentUser.tags?.filter((t: any) => t.selecionada !== false).map((t: any) => t.tag_id) ?? [],
            mol_id: currentUser.mol_id ?? null,
            descricao: currentUser.usr_descricao || '',
        });
        setShowEditProfile(true);
    };

    const handleLogout = () => {
        if (confirm('Tem certeza que deseja sair?')) {
            logout();
            navigate('/entrar');
        }
    };


    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'avatar' | 'banner') => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setMessage('error', 'A imagem deve ter no máximo 5MB.');
                return;
            }
            if (type === 'avatar') {
                setProfileForm(prev => ({
                    ...prev,
                    avatarFile: file,
                    avatarPreview: URL.createObjectURL(file)
                }));
            } else if (type === 'banner') {
                setProfileForm(prev => ({
                    ...prev,
                    bannerFile: file,
                    bannerPreview: URL.createObjectURL(file)
                }));
            }
        }
    };

    const handleTagToggle = (tagId: number) => {
        const tag = user.tags?.find((t: any) => t.tag_id === tagId);
        if (!tag) return;
        const tagPackage = tag.tag_pacote || 'Geral';

        setProfileForm(prev => {
            const isSelected = prev.selectedTags.includes(tagId);
            if (isSelected) {
                return { ...prev, selectedTags: prev.selectedTags.filter(id => id !== tagId) };
            } else {
                if (prev.selectedTags.length >= 6) {
                    setMessage('error', 'Você pode selecionar no máximo 6 tags para destacar no seu perfil.');
                    return prev;
                }

                if (tagPackage !== 'Geral') {
                    const hasTagFromSamePackage = user.tags?.some((t: any) =>
                        prev.selectedTags.includes(t.tag_id) && (t.tag_pacote || 'Geral') === tagPackage
                    );

                    if (hasTagFromSamePackage) {
                        setMessage('error', `Você já selecionou uma tag do pacote "${tagPackage}". Para este pacote, escolha apenas uma.`);
                        return prev;
                    }
                }

                return { ...prev, selectedTags: [...prev.selectedTags, tagId] };
            }
        });
    };

    const convertFileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve((reader.result as string) || '');
            reader.onerror = error => reject(error);
        });
    };

    const handleSaveProfileData = async () => {
        setSavingSection('data');
        setMessage('', '');
        try {
            await api.patch('usuarios/me', {
                usr_nome: profileForm.nome.trim(),
                usr_nick: profileForm.nick.trim(),
                usr_descricao: profileForm.descricao || null,
                tag_ids_visiveis: profileForm.selectedTags,
            });
            const fresh = await fetchUserData(true);
            if (fresh) {
                setProfileForm((prev) => ({
                    ...prev,
                    nome: fresh.usr_nome || '',
                    nick: fresh.usr_nick || '',
                }));
            }
            setMessage('success', 'Dados do perfil atualizados!');
        } catch (error: unknown) {
            console.error('Erro ao atualizar dados:', error);
            const msg =
                (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
            setMessage('error', typeof msg === 'string' && msg.trim() ? msg.trim() : 'Erro ao atualizar dados do perfil.');
        } finally {
            setSavingSection(null);
        }
    };

    const handleSaveAvatar = async () => {
        if (!profileForm.avatarFile) {
            setMessage('error', 'Escolha uma imagem de perfil.');
            return;
        }
        setSavingSection('avatar');
        setMessage('', '');
        try {
            const usr_imagem = await convertFileToBase64(profileForm.avatarFile);
            await api.patch('usuarios/me', { usr_imagem });
            const fresh = await fetchUserData(true);
            if (fresh) {
                setProfileForm((prev: any) => ({
                    ...prev,
                    avatarFile: null,
                    avatarPreview: fresh.usr_imagem ? getUserImageUrl(fresh.usr_id, fresh.usr_imagem) || '' : '',
                }));
            }
            setMessage('success', 'Foto de perfil atualizada!');
        } catch (error) {
            console.error('Erro ao atualizar avatar:', error);
            setMessage('error', 'Erro ao atualizar a foto de perfil.');
        } finally {
            setSavingSection(null);
        }
    };

    const handleSaveBanner = async () => {
        if (!profileForm.bannerFile) {
            setMessage('error', 'Escolha uma imagem de banner.');
            return;
        }
        setSavingSection('banner');
        setMessage('', '');
        try {
            const usr_banner = await convertFileToBase64(profileForm.bannerFile);
            await api.patch('usuarios/me', { usr_banner });
            const fresh = await fetchUserData(true);
            if (fresh) {
                setProfileForm((prev: any) => ({
                    ...prev,
                    bannerFile: null,
                    bannerPreview: fresh.usr_banner ? getUserBannerUrl(fresh.usr_id, fresh.usr_banner) || '' : '',
                }));
            }
            setMessage('success', 'Banner atualizado!');
        } catch (error) {
            console.error('Erro ao atualizar banner:', error);
            setMessage('error', 'Erro ao atualizar o banner.');
        } finally {
            setSavingSection(null);
        }
    };

    const handleSaveMoldura = async () => {
        const initialMolId = user.mol_id ?? null;
        if (profileForm.mol_id === initialMolId) return;

        setSavingSection('moldura');
        setMessage('', '');
        try {
            await api.patch('usuarios/me/moldura', { mol_id: profileForm.mol_id });
            const fresh = await fetchUserData(true);
            if (fresh) {
                setProfileForm((prev: any) => ({
                    ...prev,
                    mol_id: fresh.mol_id ?? null,
                    molduraPreview: fresh.moldura ? getMolduraImageUrl(fresh.moldura.mol_imagem) : null,
                    molduraTipoPreview: fresh.moldura?.mol_tipo ?? null,
                }));
            }
            setMessage('success', 'Moldura atualizada!');
        } catch (error) {
            console.error('Erro ao atualizar moldura:', error);
            setMessage('error', 'Erro ao atualizar a moldura.');
        } finally {
            setSavingSection(null);
        }
    };

    const handleSelectMoldura = (moldura: any) => {
        setProfileForm(prev => ({
            ...prev,
            mol_id: moldura ? moldura.mol_id : null,
            molduraPreview: moldura ? getMolduraImageUrl(moldura.mol_imagem) : null,
            molduraTipoPreview: moldura != null ? Number(moldura.mol_tipo) : null,
        }));
        setShowMoldurasModal(false);
    };

    const handleRequestPasswordCode = async () => {
        setPasswordLoading(true);
        setMessage('', '');
        try {
            await api.post('usuarios/password-reset/request', { email: user.usr_email });
            setPasswordStep('reset');
            setMessage('success', `Código enviado para ${user.usr_email}`);
        } catch (error) {
            console.error('Erro ao solicitar código:', error);
            setMessage('error', 'Erro ao enviar código de recuperação.');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!passwordForm.code.trim()) {
            setMessage('error', 'Insira o código enviado por e-mail.');
            return;
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setMessage('error', 'As senhas não coincidem.');
            return;
        }
        if (passwordForm.newPassword.length < 6) {
            setMessage('error', 'A senha deve ter no mínimo 6 caracteres.');
            return;
        }

        setPasswordLoading(true);
        setMessage('', '');

        try {
            await api.post('usuarios/password-reset/reset', {
                email: user.usr_email,
                code: passwordForm.code.trim(),
                newPassword: passwordForm.newPassword,
            });
            setMessage('success', 'Senha alterada com sucesso!');
            setTimeout(() => setShowPasswordModal(false), 1500);
        } catch (error: any) {
            console.error('Erro senha:', error);
            setMessage('error', error.message || 'Código inválido ou expirado.');
        } finally {
            setPasswordLoading(false);
        }
    };

    const groupedTags = useMemo(() => {
        if (!user?.tags) return {};
        const groups: Record<string, any[]> = {};

        user.tags.forEach((tag: any) => {
            const pacote = tag.tag_pacote || 'Geral';
            if (!groups[pacote]) groups[pacote] = [];
            groups[pacote].push(tag);
        });

        return groups;
    }, [user?.tags]);

    const handleImportHistorico = async () => {
        setImportHistoricoStep('running');
        setImportHistoricoProgress(null);
        try {
            const token = (import.meta as any).env.VITE_LEGACY_IMPORT_TOKEN ?? '';
            const res: any = await api.get(
                `legacy-import/admin/import-historico-leitura`,
                { params: { id: user?.usr_id, token } }
            );
            const data = (res as any)?.data ?? res;
            const total = data?.paginacao?.obras_origem_total ?? data?.total ?? 0;
            const processed = data?.paginacao?.obras_processadas_neste_pedido ?? data?.processadas ?? total;
            if (total > 0) setImportHistoricoProgress({ processed, total });
            setImportHistoricoStep('done');
        } catch (err: any) {
            setImportHistoricoStep('error');
            const msg = err?.response?.data?.message;
            setMessage('error', typeof msg === 'string' && msg.trim() ? msg.trim() : 'Erro ao importar histórico de leitura.');
        }
    };

    const setMessage = (type: 'error' | 'success' | '', text: string) => {
        setMessages({ type, text });
        if (type) setTimeout(() => setMessages({ type: '', text: '' }), 5000);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    if (!user) return null;

    return (
        <div className="min-h-screen bg-neutral-950 pb-24 relative overflow-x-hidden">
            <div className="pointer-events-none absolute inset-0 -z-0">
                <div className="absolute -top-24 -left-24 w-64 h-64 rounded-full bg-orange-500/15 blur-3xl" />
                <div className="absolute top-1/3 -right-24 w-72 h-72 rounded-full bg-orange-500/10 blur-3xl" />
            </div>
            <Navbar />

            <main className="relative z-10 max-w-6xl mx-auto px-0 md:px-8 pt-0 lg:pt-28">
                <div className="space-y-6 md:space-y-8">
                    <PerfilHeader
                        user={user}
                        isOwner={true}
                        perfilStyle="default"
                        rankingPosicao={rankingPosicao}
                        onEdit={() => openEditProfile(user)}
                    />

                    <div className="px-4 md:px-0 max-w-2xl mx-auto md:max-w-none">
                        <MeuPerfilQuickNav
                            onLogout={handleLogout}
                            onOpenPaymentsHistory={() => {
                                setShowPaymentsModal(true);
                                void fetchMyPayments();
                                void fetchMyDonations();
                            }}
                            onOpenEditProfile={() => openEditProfile(user)}
                        />
                        <PixDiscordPaymentNotice className="mb-6" />
                        <MeuPerfilGeralTab
                            user={user}
                            onOpenPasswordModal={() => {
                                setPasswordStep('request');
                                setPasswordForm({ newPassword: '', confirmPassword: '', code: '' });
                                setShowPasswordModal(true);
                            }}
                            onLinkGoogle={handleLinkGoogle}
                            onLinkDiscord={handleLinkDiscord}
                            importHistoricoStep={importHistoricoStep}
                            importHistoricoProgress={importHistoricoProgress}
                            onImportHistoricoConfirm={() => setImportHistoricoStep('confirm')}
                            onImportHistoricoRun={handleImportHistorico}
                            onImportHistoricoReset={() => {
                                setImportHistoricoStep('idle');
                                setImportHistoricoProgress(null);
                            }}
                        />
                    </div>
                </div>
            </main>

            {/* Modals */}
            <EditProfileModal
                show={showEditProfile}
                onClose={() => setShowEditProfile(false)}
                user={user}
                profileForm={profileForm}
                setProfileForm={setProfileForm}
                groupedTags={groupedTags}
                onSaveProfileData={handleSaveProfileData}
                onSaveAvatar={handleSaveAvatar}
                onSaveBanner={handleSaveBanner}
                onSaveMoldura={handleSaveMoldura}
                savingSection={savingSection}
                messages={messages}
                onOpenMolduras={openMoldurasPicker}
                fileInputRef={fileInputRef}
                bannerInputRef={bannerInputRef}
                onFileChange={handleFileChange}
                onTagToggle={handleTagToggle}
                perfilStyle="default"
            />

            <MoldurasModal
                show={showMoldurasModal}
                onClose={() => setShowMoldurasModal(false)}
                pageItems={moldurasPageItems}
                page={moldurasPage}
                totalPages={moldurasTotalPages}
                onPageChange={setMoldurasPage}
                loading={moldurasLoading}
                selectedMolId={profileForm.mol_id}
                onSelect={handleSelectMoldura}
            />

            <PasswordModal
                show={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                step={passwordStep}
                onRequestCode={handleRequestPasswordCode}
                onSubmit={handleChangePassword}
                loading={passwordLoading}
                messages={messages}
                form={passwordForm}
                setForm={setPasswordForm}
                userEmail={user.usr_email || ''}
            />

            <PaymentsHistoryModal
                show={showPaymentsModal}
                onClose={() => setShowPaymentsModal(false)}
                payments={myPayments}
                loading={paymentsLoading}
                checkingId={checkingPaymentId}
                onCheckStatus={handleCheckPaymentStatus}
                donations={myDonations}
                donationsLoading={donationsLoading}
            />
        </div>
    );
}
